"use client";
import type { IResource } from "@gettactic/api";
import React from "react";
import { MULTI_ROOM_RESERVATION_MAX_ROOMS } from "@/features/reserve/multiRoomReservation.config";
import {
  RoomSelectionContext,
  type RoomSelectionState
} from "./roomSelectionContext";
import {
  initialRoomSelectionState,
  roomSelectionReducer
} from "./roomSelectionReducer";

const maxRooms = MULTI_ROOM_RESERVATION_MAX_ROOMS;

/**
 * Holds the extra rooms of a multi-room reservation. It sits above ReserveFormProvider on
 * purpose: switching which room the reserve card shows re-creates the form, and the
 * selection has to survive that.
 */
export const RoomSelectionProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const [state, dispatch] = React.useReducer(
    roomSelectionReducer,
    initialRoomSelectionState
  );

  const addRoom = React.useCallback(
    (room: IResource) => dispatch({ type: "addRoom", room, maxRooms }),
    []
  );
  const removeRoom = React.useCallback(
    (roomId: string) => dispatch({ type: "removeRoom", roomId }),
    []
  );
  const startPicking = React.useCallback(
    () => dispatch({ type: "startPicking" }),
    []
  );
  const cancelPicking = React.useCallback(
    () => dispatch({ type: "cancelPicking" }),
    []
  );
  const clearRooms = React.useCallback(
    () => dispatch({ type: "clearRooms" }),
    []
  );

  const value: RoomSelectionState = React.useMemo(
    () => ({
      rooms: state.rooms,
      isPickingRoom: state.isPickingRoom,
      isMultiRoomSession: state.isMultiRoomSession,
      canAddRoom: state.rooms.length < maxRooms,
      addRoom,
      removeRoom,
      startPicking,
      cancelPicking,
      clearRooms
    }),
    [
      state.rooms,
      state.isPickingRoom,
      state.isMultiRoomSession,
      addRoom,
      removeRoom,
      startPicking,
      cancelPicking,
      clearRooms
    ]
  );

  return (
    <RoomSelectionContext.Provider value={value}>
      {children}
    </RoomSelectionContext.Provider>
  );
};
